import {
  hexToString, formatEther, isAddress, getAddress, decodeEventLog, decodeDeployData, decodeFunctionData
} from 'viem';
import {
  getNetwork, getAccount,
  fetchBalance,
  getPublicClient, getWalletClient,
  readContract, writeContract,
  erc20ABI, erc721ABI
} from '@wagmi/core';

import { walletAddress } from './stores.js';
import { db } from './db.js';
import { isZeroIndex, ipfsParts, ipfs2http } from './ipfs.js';

import ERC165ABI from './abis/erc165abi.json';
import ERC1155ABI from './abis/erc1155abi.json';
import BuyableFantokenABI from './abis/buyablefantokenabi.json';
import Fantoken3ABI from './abis/fantoken3abi.json';
import Album3ABI from './abis/album3abi.json';
import Album4ABI from './abis/album4abi.json';
import Album5ABI from './abis/album5abi.json';
import Collection2ABI from './abis/collection2abi.json';
import VendingABI from './abis/vendingabi.json';
import Vending2ABI from './abis/vending2abi.json';
import BomABI from './abis/bomabi.json';
import Bom2ABI from './abis/Bom2ABI.json';
import VaultABI from './abis/vaultabi.json';
import FaucetABI from './abis/faucet2abi.json';
// import FaucetABI from './abis/faucetabi.json';
// import LTERC20ABI from './abis/lterc20abi.json';
import LTERC20ABI from './abis/lterc20v2abi.json';
import EnvironmentABI from './abis/environmentabi.json';
import DynamicVaultABI from './abis/dynamicvault.json';
import ProductsABI from './abis/productsabi.json';
import ClassABI from './abis/classabi.json';

// import BomIdABI from './abis/bomidabi.json';
// import LTERC20IdABI from './abis/lterc20Idabi.json';
// import EnvironmentId from './abis/environmentId.json';
import MainIdABI from './abis/mainIdabi.json';

import BomBytecode from './bytecodes/bom.txt?raw';
import Bom2Bytecode from './bytecodes/bom2.txt?raw';
import Collection2Bytecode from './bytecodes/collection.txt?raw';
import VaultBytecode from './bytecodes/vault.txt?raw';
// import VendingBytecode from './bytecodes/vending.txt?raw';
import Vending2Bytecode from './bytecodes/vending2.txt?raw';
import FaucetBytecode from './bytecodes/faucet2.txt?raw';
// import FaucetBytecode from './bytecodes/faucet.txt?raw';
// import LTERC20Bytecode from './bytecodes/lterc20.txt?raw';
import LTERC20Bytecode from './bytecodes/lterc20v2.txt?raw';
import EnvironmentBytecode from './bytecodes/environment.txt?raw';
import DynamicVaultBytecode from './bytecodes/dynamicvault.txt?raw';
import ProductsBytecode from './bytecodes/products.txt?raw';

export const zeroAddress = '0x0000000000000000000000000000000000000000';
export const uint255MaxValue = BigInt(2**256) - BigInt(1);

export const isObject = object => object != null && typeof object === "object" &&
!Array.isArray(object);

/**
   * Serializes BigInt as number
   * @param {string} key
   * @param {any} value
   */
export const jsonReplacer = (key, value) => typeof value === 'bigint' ? Number(value) : value;

export const compressString = (s, ratio) => `${ s.slice(0, parseInt(ratio) + 2) }...${ s.slice(ratio * -1) }`

const fallbackCopyTextToClipboard = text => {
  var textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    var successful = document.execCommand('copy');
    var msg = successful ? 'successful' : 'unsuccessful';
    console.log('Fallback: Copying text command was ' + msg);
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err);
  }

  document.body.removeChild(textArea);
}

export const copyTextToClipboard = text => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text).then(function() {
    return true;
  }, function(err) {
    throw err;
  });
}

export const getBalance = async (chain, address, contract, tokenId, formatted = true) => {
  if (tokenId) {
    const [ balance, symbol ] = await Promise.all([
      getContractValue(chain, contract, 'balanceOf', [ address, tokenId ]),
      getContractJSONvalue(chain, contract, tokenId, 'symbol')
    ]);
    return symbol ? { amount: formatEther(balance), symbol } : balance;
  } else if (contract) {
    const balance = await fetchBalance({ chainId: chain, address, token: contract });
    return formatted ? balance.formatted : balance.value;
  } else {
    const balance = await fetchBalance({ chainId: chain, address });
    return formatted ? balance.formatted : balance.value;
  }
}

export const getContractType = async (contractAddress, chain) => {
  if (!isAddress(contractAddress)) return;

  chain = chain || getNetwork().chain.id;
  const contractTx = await db.txs.get({ chain, contractAddress });
  if (contractTx && Object.keys(contractTx).includes('contractType')) {
    return contractTx.contractType;
  }

  const publicClient = chain ? getPublicClient({ chainId: chain }) : getPublicClient();
  const bytecode = await publicClient.getBytecode({ address: contractAddress });
  if(!bytecode) {
    return 'wallet';
  }

  const iERC165 = '0x01ffc9a7'
  const interfacesIds = {
    Collection2: '0xd6dd17df',
    // Faucet1: '0xf7c4a8c3',
    // Faucet1_5: '0xc93aeed9',
    Faucet: '0xb2566edc',
    Faucet0dot2: '0x7b73a569',
    Faucet0dot1: '0x626ac09a',
    Vending: '0x41aedfb6',
    // Bom: '0x4af94294',
    Bom2: '0x4af94294',
    // Environment: '0x3f4be24e',
    Environment: '0x93ab50c0',
    Vault: '0x590e72fd',
    Vault0: '0x22bc3322',
    Vault01: '0x3c3f73b8',
    Vault02: '0xd3a8de5b',
    // DynamicVault: '0x03251884',
    DynamicVault: '0xf6f381d4',
    Marketplace: '0xb3d9dd38',
    FanToken3: '0x4306ef81',
    LTERC20: '0xf4a9688e',
    Album3: '0x2c81f53e',
    Album4: '0x155c489c',
    Album5: '0xb7e84585',
    Packet2: '0x5bf7dd07',
    ERC1155: '0xd9b67a26',
    ERC721: '0x80ac58cd'
  }

  const opts = {
    address: contractAddress, abi: ERC165ABI, functionName: 'supportsInterface', args: [iERC165]
  }
  if (chain) {
    opts.chainId = chain;
  }

  let isERC165;
  try {
    isERC165 = await readContract(opts);
  } catch {
    isERC165 = false;
  }
  if (isERC165) {
    try {
      console.log(await readContract({
        address: contractAddress, abi: [ MainIdABI ], functionName: 'mainId'
      }), interfacesIds['Faucet'], contractAddress)
    } catch {}
    for (const contractType in interfacesIds) {
      opts.args = [interfacesIds[contractType]];
      const isThisId = await readContract(opts);
      if (isThisId) {
        return contractType;
      }
    }
  } else {
    const asciiCode = hexToString(bytecode);
    if (asciiCode.includes('Mlkl3BuyableFanToken')) {
      return 'BuyableFanToken';
    } else if (asciiCode.includes('ERC777')) {
      return 'ERC777';
    } else if (asciiCode.includes('ERC20')) {
      return 'ERC20';
    }

    const classOpts = { address: contractAddress, abi: ClassABI, functionName: 'class' }
    if (chain) classOpts.chainId = chain;
    try {
      class_ = await readContract(classOpts);
      console.log(class_);
    } catch {}
  }

  return 'unknown';
}

export const getABI = async (contractAddress, chain) => {
  return {
    ERC20: erc20ABI,
    ERC721: erc721ABI,
    ERC1155: ERC1155ABI,
    BuyableFanToken: BuyableFantokenABI,
    FanToken3: Fantoken3ABI,
    LTERC20: LTERC20ABI,
    Album3: Album3ABI,
    Album4: Album4ABI,
    Album5: Album5ABI,
    Collection2: Collection2ABI,
    Vending: VendingABI,
    Bom: BomABI,
    Bom2: Bom2ABI,
    Vault: VaultABI,
    Vault0: VaultABI,
    Vault01: VaultABI,
    Vault02: VaultABI,
    Faucet: FaucetABI,
    Faucet0dot2: FaucetABI,
    Faucet0dot1: FaucetABI,
    Environment: EnvironmentABI,
    DynamicVault: DynamicVaultABI,
    Products: ProductsABI
  }[isAddress(contractAddress) ? await getContractType(contractAddress, chain) : contractAddress];
}

export const getBytecode = async (contractAddress, chain) => {
  return {
    LTERC20: LTERC20Bytecode,
    Collection2: Collection2Bytecode,
    Bom: BomBytecode,
    Bom2: Bom2Bytecode,
    // Vending: VendingBytecode,
    Vending: Vending2Bytecode,
    Faucet: FaucetBytecode,
    Vault: VaultBytecode,
    Environment: EnvironmentBytecode,
    DynamicVault: DynamicVaultBytecode,
    Products: ProductsBytecode
  }[isAddress(contractAddress) ? await getContractType(contractAddress, chain) : contractAddress]
}

export const getContractValue = async (chain, contractAddress, functionName, args) => {
  chain = chain || getNetwork().chain.id;
  const contractTx = await db.txs.get({ chain, contractAddress });
  const f = functionName === 'contractAddress' ? 'contractContractAddress' : functionName;
  if (contractTx && Object.keys(contractTx).includes(f)) {
    return contractTx[f];
  }
  const abi = await getABI(contractAddress, chain);
  if (!abi) {
    return null;
  }
  const result = await readContract({
    abi, address: contractAddress, chainId: chain, functionName, args
  });
  if (!args && result !== undefined && result !== null) {
    const changes = {}
    changes[f] = result;
    await db.txs.where({ chain, contractAddress }).modify(changes);
  }
  return result
}

export const getContractJSONvalue = async (chain, contractAddress, tokenId, key) => {
  const contractType = await getContractType(contractAddress, chain);
  switch (contractType) {
    case 'LTERC20':
      const tokenURI = await getContractValue(chain, contractAddress, 'tokenURI');
      const erc20json = await ipfs2http(tokenURI);
      return key ? erc20json[key] : erc20json;
      break;
    case 'Collection2':
      const uri = await getContractValue(chain, contractAddress, 'uri', [tokenId]);
      const { cId } = ipfsParts(uri);
      const pad = await isZeroIndex(cId) ? 0 : 1;
      const json = await ipfs2http(uri.replace('{id}', parseInt(tokenId) + pad));
      return key ? json[key] : json;
  }
}

const getLastBlock = async (chain, address, txType) => {
  const lastBlock = await db.txs.where({ chain, address, txType }).reverse().sortBy('blockNumber');
  return lastBlock.length ? lastBlock[0].blockNumber : 0;
}

const getScannerURL = (chain, address, fromBlock = 0, action = 'txlist', toBlock = 99999999, mod = 'account') => {
  const API_ENDPOINTS = {
    "137": "https://api.polygonscan.com/",
    "80001": "https://api-testnet.polygonscan.com/",
    "11155111": "https://api-sepolia.etherscan.io/"
  };
  const API_KEYS = {
    "137": "VQ5UQJIBN432HR6WNCJ57Y8H397ABYGSCX",
    "80001": "VQ5UQJIBN432HR6WNCJ57Y8H397ABYGSCX"
  };
  return `${ API_ENDPOINTS[chain] }/api?module=${ mod }&action=${ action }&address=${ address }&startblock=${ fromBlock }&endblock=${ toBlock }${ API_KEYS[chain] && `&apikey=${ API_KEYS[chain] }` || '' }&sort=desc`;
}

export const processInputs = async (chain, tx) => {
  const abi = await getABI(tx.to, chain);
  const decodedData = decodeFunctionData({ abi, data: tx.input });
  const member = abi.find(m => m.name === decodedData.functionName);
  const args = {};
  for(let idx = 0; idx < member.inputs.length; idx++) {
    args[member.inputs[idx].name] = { type: member.inputs[idx].type, value: decodedData.args[idx] }
  }
  if (!tx.functionName) tx.functionName = member.name;
  tx.args = args;
}

export const processConstructorInputs = async (chain, tx) => {
  const abi = await getABI(tx.contractAddress, chain);
  const bytecode = await getBytecode(tx.contractAddress);
  const decodedData = decodeDeployData({ abi, bytecode: bytecode, data: tx.input });
  const member = abi.find(m => m.type === 'constructor');
  const args = {};
  for(let idx = 0; idx < member.inputs.length; idx++) {
    args[member.inputs[idx].name] = { type: member.inputs[idx].type, value: decodedData.args[idx] }
  }
  tx.args = args;
}

const parseTx = async (chain, address, txType, tx, loading, loadingId) => {
  let abi;
  if (isAddress(tx.contractAddress)) {
    tx.contractType = await getContractType(tx.contractAddress, chain);
    // if (tx.contractType !== 'wallet') {
    //   abi = await getABI(tx.contractType);
    //   let bytecode = await getBytecode(tx.contractAddress, chain);
    //   if (bytecode.startsWith('0x')) bytecode = bytecode.slice(2);
    //   const data = tx.input.startsWith('0x') ? tx.input.slice(2) : tx.input
    //   if (abi && bytecode) {
    //     try {
    //       console.log(decodeDeployData({ abi, bytecode, data }))
    //     } catch (err) {
    //       console.log(err, bytecode, data)
    //     }
    //   }
    // }
  }

  const result = {
    ...tx,
    blockNumber: parseInt(tx.blockNumber),
    timeStamp: parseInt(tx.timeStamp),
    isError: tx.isError === '1',
    cumulativeGasUsed: parseInt(tx.cumulativeGasUsed),
    gasPrice: parseInt(tx.gasPrice),
    gasUsed: parseInt(tx.gasUsed),
    nonce: parseInt(tx.nonce),
    transactionIndex: parseInt(tx.transactionIndex),
    value: parseInt(tx.value),
    chain,
    address: getAddress(address),
    to: tx.to ? getAddress(tx.to) : tx.to,
    from: tx.from ? getAddress(tx.from) : tx.from,
    contractAddress: tx.contractAddress ? getAddress(tx.contractAddress) : tx.contractAddress,
    txType
  }

  // if (tx.functionName) {
  //   const contractType = await getContractType(tx.to, chain);
  //   abi = abi || await getABI(contractType);
  //   const decodedData = decodeFunctionData({ abi, data: tx.input });
  //   const member = abi.find(m => m.name === decodedData.functionName);
  //   const args = {}
  //   for(let idx = 0; idx < member.inputs.length; idx++) {
  //     args[member.inputs[idx].name] = { type: member.inputs[idx].type, value: decodedData.args[idx] }
  //   }
  //   result.args = args;
  // } else if (tx.to && tx.methodId && tx.methodId !== '0x') {
  //   const contractType = await getContractType(tx.to, chain);
  //   if (contractType !== 'wallet') {
  //     abi = abi || await getABI(contractType);
  //     try {
  //       const decodedData = decodeFunctionData({ abi, data: tx.input });
  //       result.functionName = decodedData.functionName;
  //       const member = abi.find(m => m.name === decodedData.functionName);
  //       const args = {}
  //       for(let idx = 0; idx < member.inputs.length; idx++) {
  //         args[member.inputs[idx].name] = { type: member.inputs[idx].type, value: decodedData.args[idx] }
  //       }
  //       result.args = args;
  //     } catch (err) {
  //       console.log(tx.to, contractType, abi, err)
  //     }
  //   }
  // }

  if (loading) loading.inc(loadingId);
  return result;
}

export const updateTxs = async (chain, address, txType = 'regular', loading) => {
  let fromBlock = await getLastBlock(chain, address, txType);
  fromBlock = fromBlock ? fromBlock + 1 : 0;
  let api_url = getScannerURL(chain, address, fromBlock, txType === 'regular' ? undefined : 'txlistinternal');
  let resp = await fetch(api_url);
  if (resp.ok) {
    const rjson = await resp.json();
    if (Array.isArray(rjson.result)) {
      let loadingId;
      if (loading) {
        loadingId = loading.init('Updating transactions {index} of {total}', rjson.result.length);
      }
      const txs = [];
      for (const tx of rjson.result) {
        const t = await parseTx(chain, address, txType, tx, loading, loadingId);
        try {
          await db.txs.add(t);
        } catch {}
        if (loading) loading.inc(loadingId);
      }
      // const txs = await Promise.all(rjson.result.map(t => parseTx(chain, address, txType, t, loading, loadingId)));

      if (loading) loading.reset(loadingId);

      // await db.txs.bulkPut(txs);
      return txs;
    }
  }
}

const getLastLogBlock = async (chain, address) => {
  const lastBlock = await db.logs.where({ chain, address }).reverse().sortBy('blockNumber');
  return lastBlock.length ? lastBlock[0].blockNumber : 'earliest';
}

const decodeLog = (log, abi) => {
  const decoded = decodeEventLog({ abi, data: log.data, topics: log.topics });
  const event = abi.find(m => m.type === 'event' && m.name === decoded.eventName)
  for (const input of event.inputs) {
    decoded.args[input.name] = { type: input.type, value: decoded.args[input.name] }
  }
  return decoded;
};

const parseLog = async (chain, address, log, abi, loading, loadingId) => {
  const decoded = decodeLog(log, abi);

  if (loading) loading.inc(loadingId);

  return {
    ...log,
    ...decoded,
    blockNumber: Number(log.blockNumber),
    chain,
    address: getAddress(address)
  }
}

export const updateLogs = async (chain, address, loading) => {
  let fromBlock = await getLastLogBlock(chain, address);
  fromBlock = !isNaN(fromBlock) ? BigInt(fromBlock + 1) : fromBlock;
  const abi = await getABI(address, chain);
  let logs = await getPublicClient().getLogs({ abi, address, fromBlock, toBlock: 'latest' });

  let loadingId;
  if (loading) {
    loadingId = loading.init('Updating logs {index} of {total}', logs.length);
  }
  logs = await Promise.all(logs.map(l => parseLog(chain, address, l, abi, loading, loadingId)));
  if (loading) loading.reset(loadingId);

  await db.logs.bulkPut(logs);
  return logs;
}

export const checkExpression = async expression => {
  let [ expr, a ] = expression.split(':');
  let args = a ? a.split('/') : undefined;

  let wallet;
  walletAddress.subscribe(w => wallet = w)();
  const expressions = {
    'wallet': async () => typeof window.ethereum !== 'undefined',
    'no-wallet': async () => typeof window.ethereum === 'undefined',
    'connected': async () => getAccount().isConnected,
    'not-connect': async () => !getAccount().isConnected,
    'balance': async () => (await getBalance(args[0], wallet)) > 0,
    'has': async () => {
      const balance = await getBalance(args[0], wallet, args[1], args[2]);
      return balance >= args[3];
    },
    'shares': async () => (await getContractValue(args[0], args[1], 'shares', [ wallet ])) > (args[2] || 0),
    'owed': async () => (await getContractValue(args[0], args[1], 'releasable', [ wallet ])) > (args[2] || 0)
  }
  return expressions[expr]()
}

export const deployContract = async (chain, contractType, data, fee, dispatch) => {
  const walletClient = await getWalletClient({ chainId: chain });
  const opts = {
    abi: await getABI(contractType),
    bytecode: await getBytecode(contractType),
    address: walletClient.account.address,
    args: data
  }
  if (fee) opts.value = fee;

  const hash = await walletClient.deployContract(opts);
  dispatch('hashed', hash);
  new CustomEvent('hashed', { detail: hash });
  const receipt = await getPublicClient().waitForTransactionReceipt({ hash });
  dispatch('deployed', receipt)
  new CustomEvent('deployed', { detail: receipt });
}

export const mint = async (chain, contract, data, dispatch) => {
  const walletClient = await getWalletClient({ chainId: chain });
}